import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageCircle, X, Send, Loader2, Bot, User } from "lucide-react";
import api from "@/lib/api";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function generateId() {
  return "chat-" + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
}

export default function ChatBubble({ botType = "public", title = "Chat with us", subtitle = "", accentColor = "sky" }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId] = useState(() => {
    const key = `lh_chat_${botType}_session`;
    let id = localStorage.getItem(key);
    if (!id) { id = generateId(); localStorage.setItem(key, id); }
    return id;
  });
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const loadedRef = useRef(false);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      if (scrollRef.current) {
        const el = scrollRef.current.querySelector("[data-radix-scroll-area-viewport]");
        if (el) el.scrollTop = el.scrollHeight;
      }
    }, 50);
  }, []);

  useEffect(() => {
    if (open && !loadedRef.current) {
      loadedRef.current = true;
      api.get(`/chat/history?session_id=${sessionId}&bot_type=${botType}`)
        .then((r) => { if (r.data.length) setMessages(r.data.map((m) => ({ role: m.role, content: m.content }))); })
        .catch(() => {});
    }
  }, [open, sessionId, botType]);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setStreaming(true);
    setMessages((prev) => [...prev, { role: "assistant", content: "" }]);

    try {
      const endpoint = botType === "tenant" ? "/api/chat/tenant" : "/api/chat/public";
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6).trim();
            if (data === "[DONE]") break;
            try {
              const parsed = JSON.parse(data);
              if (parsed.content) {
                setMessages((prev) => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last && last.role === "assistant") {
                    updated[updated.length - 1] = { ...last, content: last.content + parsed.content };
                  }
                  return updated;
                });
                scrollToBottom();
              }
            } catch {}
          }
        }
      }
    } catch (err) {
      setMessages((prev) => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last && last.role === "assistant" && !last.content) {
          updated[updated.length - 1] = { ...last, content: "Sorry, I couldn't connect. Please try again." };
        }
        return updated;
      });
    } finally {
      setStreaming(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const colors = {
    sky: { bg: "bg-sky-500", hover: "hover:bg-sky-600", ring: "ring-sky-500/30", header: "bg-sky-500", light: "bg-sky-50 text-sky-800" },
    amber: { bg: "bg-amber-500", hover: "hover:bg-amber-600", ring: "ring-amber-500/30", header: "bg-amber-500", light: "bg-amber-50 text-amber-800" },
  };
  const c = colors[accentColor] || colors.sky;

  return (
    <>
      {/* Floating Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className={`fixed bottom-6 right-6 z-50 ${c.bg} ${c.hover} text-white h-14 w-14 rounded-full shadow-xl flex items-center justify-center transition-all duration-300 hover:scale-110 ring-4 ${c.ring}`}
          data-testid={`chat-bubble-${botType}`}
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-4rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-scale-in" data-testid={`chat-panel-${botType}`}>
          {/* Header */}
          <div className={`${c.header} text-white px-5 py-4 flex items-center justify-between shrink-0`}>
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center">
                <Bot className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-sm leading-tight">{title}</h3>
                {subtitle && <p className="text-white/70 text-[11px]">{subtitle}</p>}
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="h-8 w-8 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors" data-testid={`chat-close-${botType}`}>
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Messages */}
          <ScrollArea className="flex-1 p-4" ref={scrollRef}>
            <div className="space-y-4">
              {messages.length === 0 && (
                <div className="text-center py-8">
                  <Bot className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                  <p className="text-sm text-slate-400">
                    {botType === "public"
                      ? "Hi! Ask me anything about Legacy Heights accommodation."
                      : "Hi! I'm here to help with your tenancy. Ask me anything."}
                  </p>
                </div>
              )}
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    msg.role === "user"
                      ? `${c.bg} text-white rounded-br-md`
                      : "bg-slate-100 text-slate-800 rounded-bl-md"
                  }`}>
                    {msg.content || (
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        <span className="text-xs opacity-70">Thinking...</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>

          {/* Input */}
          <div className="border-t border-slate-100 p-3 shrink-0">
            <div className="flex items-center gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type a message..."
                className="flex-1 h-10 border-slate-200 rounded-full px-4 text-sm"
                disabled={streaming}
                data-testid={`chat-input-${botType}`}
              />
              <Button
                onClick={handleSend}
                disabled={!input.trim() || streaming}
                className={`h-10 w-10 rounded-full ${c.bg} ${c.hover} text-white p-0 shrink-0`}
                data-testid={`chat-send-${botType}`}
              >
                {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
